<script setup lang="ts">
// ⚠️ Could not locate Tag.vue
import Tag from 'primevue/tag'
import Button from '@/components/Button.vue'

const { $content } = useNuxtApp()

const router = useRouter()
const productsStore = useProductsStore()

const props = withDefaults(defineProps<{
  item: {
    titles: Record<string, string>,
    description: {
      short: string,
      full: string,
    },
    category: string,
    categoryName?: string,
    image: string,
    slug: string,
    images: string[],
    origin?: string,
    denaturation?: string,
  }
}>(), {
  item: () => {
    return {
      titles: {
        card: "{Product Card Title}",
        admin: "Application Name",
        short: "Application Name",
        long: "Application Name",
        page: "Application Name",
        introHairline: "Application Name",
      },
      description: {
        short: "Application Description",
        full: "Application Description",
      },
      category: "",
      image: "/images/12.jpg",
      slug: "",
      images: [],
    }
  },
})

const redirect = (slug: string) => {
  router.push(`/product/${slug}`)
}


const productCategoryObj = computed(() => productsStore.getCategoryBySlug(props.item.category))

const category = computed(() => props.item?.category)
const categoryLabel = computed(() => {
  // Use the categoryName prop if provided, otherwise fall back to the old logic
  if (props.item.categoryName) {
    return props.item.categoryName
  }
  
  // Fallback to old logic for backwards compatibility
  if (category.value) {
    switch (category.value) {
      case 'ethanol':
        return 'Ethanol Blends'
      case 'essential-chemicals':
        return 'Essential Chemicals'
      case 'manufactured-products':
        return 'Manufactured Products'
      default:
        return category.value
    }
  }
})

const categoryColor = computed(() => {
  return productCategoryObj.value?.color || '#000000'
})

// Helper function to decode HTML entities
const decodeHtmlEntities = (text: string): string => {
  if (typeof document === 'undefined') {
    // Server-side fallback
    return text
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#039;/g, "'")
      .replace(/&nbsp;/g, ' ')
  }
  const textarea = document.createElement('textarea')
  textarea.innerHTML = text
  return textarea.value
}

// Computed property for decoded category label
const decodedCategoryLabel = computed(() => {
  const label = categoryLabel.value
  return label ? decodeHtmlEntities(label) : ''
})

// Computed property for safe description
const safeDescription = computed(() => {
  const desc = props.item.description?.short
  if (!desc || desc === 'undefined' || desc === 'null' || desc === '') {
    return '' // Return empty string instead of showing "undefined"
  }
  return desc
})

// Computed property for decoded title
const decodedTitle = computed(() => {
  const title = props.item.titles?.card
  return title ? decodeHtmlEntities(title) : ''
})

function getImageUrl(slug: string, image: string) {
  // If image is already a full URL, return it as-is
  if (image && (image.startsWith('http://') || image.startsWith('https://'))) {
    return image
  }
  // Otherwise, build the local path
  return image && slug ? `/images/products/${slug}/${image}` : '/product-placeholder.jpg'
}

const imagesArr = computed(() => {
  return props.item.images.map(image => getImageUrl(props.item.slug, image))
})


const activeIndex = ref(0)

function nextImage() {
  activeIndex.value = (activeIndex.value + 1) % imagesArr.value.length
}

function prevImage() {
  // + imagesArr.value.length to avoid negative wrap issues
  activeIndex.value = (activeIndex.value - 1 + imagesArr.value.length) % imagesArr.value.length
}
</script>

<template>
  <Frame class="product-card" w="max-w-90% mx-auto flex flex-col g-0.5 gmb-2">
    <Frame class="sec-card-header  relative" w="w-full h-full flex items-center">

      <!-- <div class="sec-tags">
        <div class="tags top-left">
          <Tag class="new" severity="info" value="new" />

        </div>
      </div> -->

      <Frame class="sec-image" w="h-full flex justify-center items-center">
        <!-- <img :src="`/products/${item.slug}/${item.image}`" :alt="item.titles?.card" /> -->

        <div v-if="imagesArr.length > 0" class="image-wrapper">
          <div
            v-for="(img, index) in imagesArr"
            :key="index"
            class="fade-image"
            :class="{ active: index === activeIndex }"
          >
          
            <img :src="img" :alt="item.titles?.card" />
          </div>
        </div>
        <div v-else class="image-wrapper flex justify-center items-center">
          <img :src="item.image ? item.image : '/placeholder.jpg'" :alt="item.titles?.card" />
        </div>

        <!-- BOTTOM-RIGHT ARROWS -->
         <template v-if="imagesArr.length > 1">
          <button class="arrow-button left-arrow z-5" @click="prevImage">
            <i class="flex i i-mdi:chevron-left"></i>
          </button>
          <button class="arrow-button right-arrow z-5" @click="nextImage">
              <i class="flex i i-mdi:chevron-right"></i>
          </button>
        </template>

      </Frame>

      <div v-if="category" class="sec-item-code" :class="category">
        <span>{{ decodedCategoryLabel }}</span>
      </div>

      <!-- <div v-if="item.origin" class="card-label origin-label" :class="item.origin">
        <span> {{ item.origin }} </span>
      </div>

      <div v-if="item.denaturation" class="card-label denatured-label" :class="item.denaturation">
        <span> {{ item.denaturation }} </span>
      </div> -->
    </Frame>
    <Frame class="sec-card-body">

      <Wrap class="max-w-85% mx-auto grid g-0.0">
        <div class="sec-title grid g-0.25">
          <div class="title"> {{ decodedTitle }} </div>
        </div>

        <div class="sec-qualities">

          <div v-if="item.origin" class="card-label origin-label" :class="item.origin">
            <span> {{ item.origin }} </span>
          </div>

          <div class="divider" v-if="item.origin && item.denaturation" />

          <div v-if="item.denaturation" class="card-label denatured-label" :class="item.denaturation">
            <span> {{ item.denaturation }} </span>
          </div>
        </div>


        <div v-if="safeDescription" class="sec-description">
          {{ safeDescription }}
        </div>
      </Wrap>


    </Frame>

    <Frame class="sec-card-footer" w="max-w-85% mx-auto">

      <div class="sec-actions gpt-0.5 gpb-1">
        <Button btn="blue" class="gpy-0.33 gpx-1 fs--1 fw-600 flex items-center g-0.5" @click="redirect(item.slug)">
          
          <div class="sec-label">
            View
          </div>
          <div class="sec-icon">
            <i class="i i-mdi:arrow-right flex "></i>
          </div>

        </Button>
      </div>
    </Frame>
  </Frame>
</template>

<style lang="scss" scoped>
.product-card {

  /* This is the background container */
  /* min-height: 300px; */
  width: 100%;
  /* width: 400px; */

  display: flex;
  flex-direction: column;

  :deep(>.m-wrap) {
    flex: 1;
    @apply shadow-xl;
    background: white;
    width: 100%;
    height: 100%;
    @apply rounded-xl;
    overflow: hidden;





    .sec-card-header {
      /* min-height: 300px; */

      .sec-tags {
        position: absolute;
        top: gr(0.5);
        left: gr(0.5);
        /* width: 100%; */
        /* height: 100%; */
        z-index: 1;

        .new {
          background: transparent;
          color: white;
          color: var(--base-600);
          font-weight: 400;
          font-size: 11px;
          text-transform: uppercase;
          @apply tracking-wider;
          border: 1px solid var(--base-500);
          border-radius: 30px;
          padding: 0.125rem 0.5rem;
        }
      }

      .sec-image {
        overflow: hidden;
        display: flex;
        justify-content: center;
        align-items: center;
        aspect-ratio: 4 / 2.5; /* explicitly define the aspect ratio */
        /* box-shadow: inset 0 0 0 1px red; */
        border-bottom: 1px solid #f1f1f1;
        padding-top: gr(1);
        

        width:100%;

        /* img { */
        /*   width: auto; */
        /*   height: 100%; utilize the full height of the container */
        /*   object-fit: cover; maintain aspect ratio and fill the container */
        /*   object-position: center; center the image within the container */
        /*   transition: all 0.3s ease-in-out; */
        /* } */

        .image-wrapper {
          position: relative;
          width: 100%;
          height: 100%;
          overflow: hidden; /* ensure images don't spill out */

          img {
            width: auto;
            height: 100%; /* utilize the full height of the container */
            object-fit: cover; /* maintain aspect ratio and fill the container */
            object-position: center; /* center the image within the container */
            transition: all 0.3s ease-in-out;
          }
        }

        .fade-image {
          position: absolute;
          inset: 0; /* top: 0; right: 0; bottom: 0; left: 0; */
          opacity: 0;
          transition: opacity 0.2s ease-in-out;

          display: flex;
          justify-content: center;
          align-items: center;

          overflow: hidden;

          img {
            width: auto;
            height: 100%; /* utilize the full height of the container */
            object-fit: cover; /* maintain aspect ratio and fill the container */
            object-position: center; /* center the image within the container */
            transition: all 0.3s ease-in-out;
            /* border: 1px solid red; */
            /* aspect-ratio: 1 / 1; */
            object-position: center;
            position: relative;
          }
        }

        /* When active, show the image */
        .fade-image.active {
          opacity: 1;
          z-index: 1;
        }

        /* Arrow buttons in bottom-right corner */
        .arrow-button {
          position: absolute;
          bottom: 0.5rem;
          background: rgba(255, 255, 255, 0.85);
          border: none;
          cursor: pointer;
          padding: gr(0.2) gr(0.25);
          border-radius: 0.25rem;
          font-size: 1rem;
          color: #333;
          /* Adjust as desired */
          /* background: red; */
          border: 1px solid var(--base-250);

          i {
            color: var(--base-800);
          }

          &:hover {
           transform: translateY(-1px);
           i {
            color: var(--i-blue);
           }

           &:active {
            transform: translateY(0px);
           }
          }

          &.left-arrow {
            right: 2.9rem;
          }

          &.right-arrow {
            right: 0.5rem;
          }
        }
      }

      .card-label {
        position: absolute;
        bottom: gr(0.3);
        left: gr(0.75);
        transform: 1px;
        padding: gr(0.075) gr(0.33) gr(0.075) gr(0.33);
        font-size: 11px;
        text-transform: uppercase;
        @apply tracking-wider;
        font-weight: 500;
        border-radius: 30px;
        background: white;
        transition: all 0.2s ease;
        /* border: 1px solid var(--base-200); */

        &.origin-label {

          &.natural {
            color: var(--emerald-400);
            border: 1px solid transparent;
            transform: translateX(-5px);
          }

          &.synthetic {
            color: var(--indigo-500);
            border: 1px solid transparent;
            transform: translateX(-5px);
          }

        }

        &.denatured-label {
          left: auto;
          right: gr(0.5);

          &.denatured {
            color: var(--pink-400);
            border: 1px solid transparent;
          }

          &.undenatured {
            color: var(--c-sky-blue);
            border: 1px solid transparent;
            /* border: 1px solid var(--c-blue-500); */
          }
        }
      }

      .sec-category {
        background: white;
        position: absolute;
        top: gr(0.5);
        right: gr(0.5);
        transform: 1px;
        padding: gr(0.1125) gr(0.33) gr(0.1125) gr(0.33);
        font-size: 11px;
        text-transform: uppercase;
        @apply tracking-wider;
        font-weight: 500;
        border-radius: 30px;
        border: 1px solid var(--base-500);
      }

      .sec-item-code {
        background: white;
        position: absolute;
        top: 0;
        left: 0;
        transform: translateY(-1px);
        padding: gr(0.225) gr(0.75) gr(0.1125) gr(0.5);
        font-size: 11px;
        text-transform: uppercase;
        @apply tracking-wide;
        font-weight: 500;
        z-index: 1;
        /* box-shadow: inset 0 0 0 1px rgba(0, 0, 255, 0.1); */


        transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;
        /* box-shadow: inset 0 0 0 1px rgba(0, 0, 255, 0.1); */

        span {
          position: relative;
          display:inline-block;
          /* transform: translateY(-1px); */
        }

        &.ethanol {
          background: var(--sky-50);
          color: var(--sky-600);
        }

        &.manufactured-products {
          background: var(--emerald-50);
          color: var(--emerald-600);
        }

        &.essential-chemicals {
          background: var(--purple-50);
          color: var(--purple-500);
        }

        /* border-radius: 0 0.25rem 0 0; */
      }
    }

    .sec-card-body {
      flex: 1;

      transition: all 0.2s ease;

      .sec-title {
        padding-top: gr(0.25);
        margin-bottom: gr(0.5);
        .title {
          font-size: fs(1.5);
          font-weight: 400;
          font-size: 22px;
          line-height: gr(1.2);
          color: rgb(4, 61, 117);
        }
      }

      .sec-qualities {
        display: flex;
        flex-direction: row;
        gap: gr(0.5);
        margin-bottom: gr(0.75);

        .card-label {
          text-transform: uppercase;
          font-size: fs(-1);
          font-weight: 500;
          color: var(--base-500);


          &.origin-label {

            &.natural {
              /* background: var(--green-500); */
              /* color: white; */
              background: transparent;
              color: var(--emerald-400);
              border-color: var(--emerald-400);
            }

            &.synthetic {
              background: transparent;
              color: var(--indigo-500);
              border-color: var(--indigo-500);
            }

          }

          &.denatured-label {
          

            &.denatured {
              background: transparent;
              color: var(--pink-500);
              border-color: var(--pink-500);
            }

            &.undenatured {
              background: transparent;
              color: var(--c-sky-blue);
              border-color: var(--c-sky-blue);
            }
          }
        }
        
        .divider {
          width: 1px;
          height: 100%;
          background: var(--base-400);
        }
        
      }

      .sec-description {
        color: var(--base-650);
        font-size: fs(-0.5);
      }
    }

    .sec-card-footer {
      flex: 1;

      .p-button {
        &.btn-product {
          padding: gr(0.3) gr(0.7);
          font-size: fs(-1);

          gap: gr(0.2);

          background: white;
          color: var(--i-blue);
          border-color: var(--i-blue);
          border: 1px solid var(--i-blue);

          .sec-icon {
            width: 16px;
            height: 16px;
            margin-left: gr(0.25);
            transform: translateY(-1.5px);
          }

          .sec-label {
            font-size: fs(-15);
          }

          &:hover {
            /* background: var(--i-yellow-green); */
            background: var(--i-blue);
            color: white;
          }
        }
      }
    }

    .sec-card-footer {
      flex: 1;
    }

    &:hover {
      .sec-card-header {
        .sec-image {
          img {
            transform: scale(1.1);
          }
        }

        .sec-item-code {
          /* color: var(--c-sky-blue); */
          /* background: #22beff; */
          color: white;

          &.ethanol {
            /* background: var(--sky-300); */
            color: white;
            background: var(--sky-400);
          }

          &.manufactured-products {
            /* background: var(--emerald-400); */
            color: white;
            background: var(--emerald-400);
          }

          &.essential-chemicals {
            background: var(--purple-400);
            color: white;
          }
        }

        .card-label {
          position: absolute;
          transform: 1px;
          /* padding: gr(0.1125) gr(0.33) gr(0.1125) gr(0.33); */
          text-transform: uppercase;
          @apply tracking-wider;
          font-weight: 500;
          border-radius: 30px;
          background: white;


          &.origin-label {

            &.natural {
              /* background: var(--green-500); */
              /* color: white; */
              background: transparent;
              color: var(--emerald-400);
              border-color: var(--emerald-400);
            }

            &.synthetic {
              background: transparent;
              color: var(--indigo-500);
              border-color: var(--indigo-500);
            }

          }

          &.denatured-label {
            left: auto;
            right: gr(0.5);

            &.denatured {
              background: transparent;
              color: var(--pink-500);
              border-color: var(--pink-500);
            }

            &.undenatured {
              background: transparent;
              color: var(--c-sky-blue);
              border-color: var(--c-sky-blue);
            }
          }
        }
      }

      .sec-card-body {
        transform: translateY(-1px);

        .sec-title {
          .title {
            /* color: var(--c-sky-blue); */
            color: #0051a8;
          }
        }
      }

      .sec-card-footer {
        display: block;


      }
    }
  }



}
</style>